<template>
  <section class="task-chat">
    <h3 class="task-chat__title">Комментарии</h3>
    <ul class="task-chat__comments" ref="comments">
      <li
        v-for="comment in task.comments"
        v-bind:key="comment.id"
        class="task-chat__comment"
      >
        <img
          v-if="comment.authorPhotoUrl"
          class="user-avatar task-chat__avatar"
          v-bind:src="comment.authorPhotoUrl"
          v-bind:alt="`Фотография профиля ${comment.authorName}`">
        <div v-else class="task-chat__avatar user-avatar user-avatar--default">
          {{ comment.authorInitials }}
        </div>
        <span class="task-chat__name">{{ comment.authorName}}</span>
        <p class="task-chat__message">{{ comment.text }}</p>
      </li>
    </ul>
    <form
      class="form-comment task-chat__submit-form"
      v-on:submit.prevent="onSubmit"
      autocomplete="off"
    >
      <div class="form-comment__message">
        <img
          v-if="userPhotoUrl"
          class="user-avatar form-comment__avatar"
          v-bind:src="userPhotoUrl"
          v-bind:alt="`Фотография профиля ${task.authorName}`">
        <div v-else class="user-avatar user-avatar--default">
          {{ userInitials }}
        </div>
        <textarea-length-limitation
          class="form-comment__textarea"
          v-model="text"
          v-bind:max-length="$options.MAX_COMMENT_LENGTH"
          v-bind:violations="textInputErrors"
          placeholder="Ваш комментарий..."
          rows="1"
        />
      </div>
      <button
        class="form-comment__button"
        type="submit"
        v-bind:disabled="!text.length">
        Отправить
      </button>
    </form>
  </section>
</template>

<script>
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';

/**
 * @description Максимальная длина комментария.
 * @type {string}
 */
const MAX_COMMENT_LENGTH = 1000;

/**
 * @description Название события отправки формы.
 * @type {string}
 */
const NAME_EVENT_SUBMIT = 'submit';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Чат для поставленной задачи.
 * @type {Vue}
 */
export default {
  MAX_COMMENT_LENGTH,
  components: {
    TextareaLengthLimitation,
  },
  computed: {
    textInputErrors: resolveInputErrors('comment.text'),
    userPhotoUrl() {
      return this.$store.getters.getProfile.photo_url;
    },
    userInitials() {
      return this.$store.getters.getProfile.initials;
    },
  },
  data() {
    return {
      /**
       * @description Текст комментария
       * @type {string}
       */
      text: '',
    };
  },
  name: 'task-chat',
  watch: {
    task() {
      this.scrollCommentListToEnd('smooth');
    },
  },
  methods: {
    /**
     * @description Проскроллить список комментариев вниз.
     * @returns {void}
     */
    scrollCommentListToEnd(scrolMode = 'auto') {
      this.$nextTick(() => {
        const { comments } = this.$refs;
        if (comments) {
          comments.scrollTo({ top: comments.scrollHeight, behavior: scrolMode });
        }
      });
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      this.$emit(NAME_EVENT_SUBMIT, this.text);
      this.text = '';
    },
  },
  props: {
    task: { required: true, type: Object },
    initText: { required: false, type: String, default: () => '' },
    inputErrors: { required: true, type: Object },
  },
  created() {
    this.scrollCommentListToEnd();
    this.text = this.initText;
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

  .task-chat {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    width: 100%;
    height: 60dvh;
    &__title {
      margin-bottom: 10px;
      font-size: 18px;
      font-weight: 500;
      color: var(--reading-color);
    }
    &__comments {
      display: flex;
      flex-direction: column;
      row-gap: 12px;
      max-height: 600px;
      margin: 0;
      padding: 0;
      list-style: none;
      padding-block: 5px;
      border-top: 1px solid var(--border-color);
      overflow-y: auto;
    }
    &__comment {
      display: grid;
      grid-template-columns: auto 1fr;
      align-items: center;
      gap: 4px 14px;
      padding-block: 10px;
    }
    &__name {
      font-weight: 600;
      color: var(--reading-color);
    }
    &__message {
      grid-column: 2/3;
      width: fit-content;
      padding: 16px 15px;
      color: #323C47;
      line-height: normal;
      hyphens: auto;
      overflow-wrap: anywhere;
      white-space: pre-wrap;
      background-color: var(--fill-color);
      border-radius: 4px;
    }
    &__submit-form {
      margin-top: auto;
    }
  }

.user-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  object-fit: cover;
  &--default {
    font-weight: bold;
    line-height: 34px;
    text-align: center;
    color: white;
    background-color: var(--action-color);
  }
}

.form-comment {
  display: grid;
  row-gap: 10px;
  padding-block: 24px 0;
  &__message {
    display: flex;
    column-gap: 14px;
  }
  &__avatar {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    object-fit: cover;
  }
  &__textarea {
    flex: 1;
  }
  &__button {
    @include buttons.btn-blue(10rem, 2.5rem, 1rem);
    justify-self: end;
  }
}

@media screen and (max-width: 480px) {
  .task-chat {
    &__comments {
      row-gap: 5px;
    }
    &__message {
      padding: 12px;
    }
  }

  .form-comment {
    padding-top: 15px;
  }
}
</style>
